import { useCallback, useMemo } from "react";
import { useToast } from "../../components/ui/toast-context-provider";
import * as Yup from "yup";
import { useQueryClient } from "react-query";
import { Form, Formik } from "formik";
import { EditContentHeader } from "../../components/ui/edit-content-header";
import { classNames } from "primereact/utils";
import FormikInputSwitch from "../../components/ui/formik/FormikInputSwitch";
import FormikDropDown from "../../components/ui/formik/FormikDropdown";
import { useHorseGroupsQuery } from "../../queries/horse-groups.query";
import {
  useGetUserByIdQuery,
  useUserSettingsMutation,
} from "../../queries/models/_shared/auth.query";
import { authService } from "../../services/auth.service";
import { UserSettingsRequest } from "../../queries/models/user-settings.model";

export interface UserSettingsFormProps {
  onSave: () => Promise<any>;
}

export function UserSettingsForm({ onSave }: UserSettingsFormProps) {
  const lg = false;
  const toast = useToast();
  const queryClient = useQueryClient();
  const userQuery = useGetUserByIdQuery(authService.getLoggedUser()?.id);
  const setAppSettingsMutation = useUserSettingsMutation();
  const hasWriteAccess = true;

  const horseGroupsQuery = useHorseGroupsQuery();

  const groupOptions = useMemo(() => {
    const opts = horseGroupsQuery.data?.map((x) => ({ label: x.name, value: x.id })) ?? [];
    return [{ label: "— Brak —", value: undefined }, ...opts];
  }, [horseGroupsQuery.data]);

  const config = useMemo(() => {
    return userQuery.data;
  }, [userQuery.data]);

  const initialValues: UserSettingsRequest = {
    userId: config?.id ?? 0,
    usePlanShortcuts: config?.usePlanShortcuts ?? false,
    usePlanOnlyMyRides: config?.usePlanOnlyMyRides ?? false,
    showTreatmentsInPlan: config?.showTreatmentsInPlan ?? false,
    showNotRiddenFromGroupId: config?.showNotRiddenFromGroupId ?? null,
  };
  const validationSchema = Yup.object({
    userId: Yup.string().required("Required"),
    usePlanShortcuts: Yup.boolean().required("Required"),
    usePlanOnlyMyRides: Yup.boolean().required("Required"),
    showTreatmentsInPlan: Yup.boolean().required("Required"),
    showNotRiddenFromGroupId: Yup.number().optional().nullable(),
  });

  const onSubmit = useCallback(
    async (values: UserSettingsRequest) => {
      try {
        await setAppSettingsMutation.mutateAsync(values, {
          onSuccess: async () => {
            await queryClient.invalidateQueries("usersettings");
            await queryClient.invalidateQueries("authuser");
            toast.current?.show({
              severity: "success",
              detail: "Zapisano!",
            });
          },
        });
        onSave();
      } catch (error) {
        // Optional: handle error
        console.error("Submission error:", error);
      }
    },
    [setAppSettingsMutation, onSave, queryClient, toast]
  );

  return (
    <div className="p-3 h-full w-full">
      {true && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange
          validateOnMount
        >
          {(formik) => (
            <Form
              autoComplete="off"
              className="flex flex-column h-full w-full overflow-hidden"
            >
              <div className="formgrid mb-2 overflow-auto h-full">
                <div className="field col-12 md:col-12">
                  <FormikInputSwitch
                    label="Użyj skrótów nazw"
                    name="usePlanShortcuts"
                    validationSchema={validationSchema}
                    isIndependent
                    disabled={!hasWriteAccess}
                    checked={false}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <FormikInputSwitch
                    label="Pokazuj tylko moje jazdy"
                    name="usePlanOnlyMyRides"
                    validationSchema={validationSchema}
                    isIndependent
                    disabled={!hasWriteAccess}
                    checked={false}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <FormikInputSwitch
                    label="Pokaż zabiegi w planie"
                    name="showTreatmentsInPlan"
                    validationSchema={validationSchema}
                    isIndependent
                    disabled={!hasWriteAccess}
                    checked={false}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <FormikDropDown
                    label="Pokaż niejeżdżace z grupy"
                    name="showNotRiddenFromGroupId"
                    options={groupOptions}
                    validationSchema={validationSchema}
                    isIndependent
                    disabled={!hasWriteAccess}
                  />
                </div>
              </div>

              <EditContentHeader
                header={"Ustawienia użytkownika"}
                showDeleteButton={false}
                showCancelButton={false}
                saveButtonDisabled={!formik.dirty || !formik.isValid}
                onSaveClick={async () => {
                  if (!formik.isValid) {
                    toast.current?.show({
                      severity: "error",
                      detail: "Form invalid",
                    });
                    return;
                  }
                  return formik.submitForm();
                }}
                onCancelClick={() => {}}
              />
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}
